/** Configuration and operations pages.
 *
 * Compute is the one screen that shows infrastructure, and it is deliberately
 * separate: everywhere else the person thinks about pods, apps and data, not
 * about the machines underneath.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AppManifest, Artifact, EventRow, ModelRow, ProcessSummary, Source,
  StorageDestination, SyncGroup, WorkerRow, api,
} from "../lib/api";
import { bytes, clockTime, number, relative } from "../lib/format";
import { useAsync, usePolling } from "../lib/useLive";
import {
  ArtifactRow, ConfirmDelete, Empty, ErrorBanner, Loading, Modal, PageHeader, Segmented, SourceStatus, StateBadge,
} from "../components/ui";
import { Icon } from "../components/icons";

// ------------------------------------------------------------- HoloPods

export function HoloPods() {
  const { data, error, loading, reload } = useAsync<Source[]>(() => api.sources(), []);
  const [adding, setAdding] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState<Source | null>(null);
  const [force, setForce] = useState(false);

  const test = async (id: string) => {
    setTesting(id);
    try {
      const result = await api.testSource(id);
      setResults({
        ...results,
        [id]: result.ok
          ? `Connected · ${result.width}×${result.height} @ ${result.fps ?? "?"} fps`
          : `Could not connect: ${result.error}`,
      });
      reload();
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="page stack">
      <PageHeader
        title="HoloPods"
        description="Cameras and streams HoloXI can process. Testing a pod uses the same connection code a worker does."
        actions={<button className="btn primary" onClick={() => setAdding(true)}><Icon name="plus" size={14} />Add source</button>}
      />

      {error && <ErrorBanner error={error} onRetry={reload} />}
      {loading && <Loading rows={3} />}

      {!loading && (data?.length ?? 0) === 0 && (
        <Empty
          icon="holopods"
          title="No sources yet"
          body="Add a HoloPod, RTSP camera, or a simulated stream to start building processes."
          action={<button className="btn primary" onClick={() => setAdding(true)}><Icon name="plus" size={14} />Add source</button>}
        />
      )}

      <div className="grid cols-3">
        {(data ?? []).map((source) => (
          <div className="card stack" key={source.id}>
            <div className="row">
              <SourceStatus status={source.status} />
              <div className="spacer" />
              <span className="tag">{source.kind}</span>
            </div>
            <div className="preview pod-preview">
              {source.kind === "holopod" && source.status !== "ONLINE" ? (
                <div className="preview-placeholder">
                  Test the connection to pull a frame from this LiveSwitch channel
                </div>
              ) : (
                <img src={api.previewUrl(source.id)} alt={`${source.name} preview`}
                     onError={(event) => { (event.target as HTMLImageElement).style.opacity = "0.15"; }} />
              )}
              {source.active_runs > 0 && (
                <div className="preview-badge">
                  <span className="led running" />
                  {source.active_runs} live
                </div>
              )}
            </div>
            <div>
              <div className="card-title">{source.name}</div>
              <div className="hint">{source.location || source.description}</div>
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
              {source.device_id ?? source.id}
              {typeof source.config.channel_id === "string" && source.config.channel_id
                ? ` · channel ${source.config.channel_id}`
                : ""}
              {source.width ? ` · ${source.width}×${source.height}` : ""}
              {source.fps ? ` · ${source.fps} fps` : ""}
            </div>
            {results[source.id] && <p className="hint" style={{ margin: 0 }}>{results[source.id]}</p>}
            <div className="row">
              <button className="btn sm" disabled={testing === source.id} onClick={() => test(source.id)}>
                {testing === source.id ? "Testing…" : "Test connection"}
              </button>
              <Link className="btn sm ghost" to="/processes/new">Use in a process</Link>
              <div className="spacer" />
              <button className="btn sm ghost" style={{ color: "var(--coral)" }}
                      disabled={source.active_runs > 0}
                      title={source.active_runs > 0
                        ? "A process is reading from this pod. Stop it first."
                        : undefined}
                      onClick={() => { setRemoving(source); setForce(false); }}>
                <Icon name="trash" size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {adding && <AddSource onClose={() => { setAdding(false); reload(); }} />}

      {removing && (
        <ConfirmDelete
          title="Delete this source?"
          name={removing.name}
          confirmLabel="Delete source"
          onClose={() => setRemoving(null)}
          onConfirm={async () => { await api.deleteSource(removing.id, force); reload(); }}
          body={
            <p style={{ marginTop: 0 }}>
              The source and its device details are removed. Processes that use it are kept, with
              their run history, but will have no source until you pick a new one.
            </p>
          }
          extra={
            <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
              <input type="checkbox" checked={force} style={{ marginTop: 3 }}
                     onChange={(event) => setForce(event.target.checked)} />
              <span style={{ fontSize: 13 }}>
                Detach any processes still using it
                <span className="hint" style={{ display: "block" }}>
                  Required if this pod is referenced by a process.
                </span>
              </span>
            </label>
          }
        />
      )}
    </div>
  );
}

function AddSource({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("rtsp");
  const [url, setUrl] = useState("");
  const [channelId, setChannelId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [location, setLocation] = useState("");
  const [loop, setLoop] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const config = kind === "demo"
    ? { width: 1280, height: 720, fps: 15 }
    : kind === "file"
      ? { stream_url: url, loop }
      : kind === "holopod"
        ? { channel_id: channelId.trim() }
        : { stream_url: url };

  const canSubmit = Boolean(name.trim()) && (kind !== "holopod" || Boolean(channelId.trim()));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.createSource({
        name, kind, adapter: kind, location, config,
        credentials: kind === "holopod" || !(username || password) ? {} : { username, password },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Add a video source" onClose={onClose}>
      <div className="stack">
        {error && <ErrorBanner error={error} />}
        <label className="field">
          Name
          <input type="text" value={name} placeholder="OR-2 ceiling camera"
                 onChange={(event) => setName(event.target.value)} />
        </label>
        <label className="field">
          Type
          <select value={kind} onChange={(event) => setKind(event.target.value)}>
            <option value="rtsp">RTSP camera</option>
            <option value="holopod">HoloPod device</option>
            <option value="srt">SRT stream</option>
            <option value="file">Video file</option>
            <option value="demo">Simulated pod (for trying things out)</option>
          </select>
        </label>
        {kind === "holopod" && (
          <>
            <label className="field">
              Channel ID
              <input type="text" value={channelId} placeholder="channel-01"
                     onChange={(event) => setChannelId(event.target.value)} />
            </label>
            <p className="hint">
              The worker joins this LiveSwitch channel using the gateway, application ID
              and shared secret configured on the server. Those values are never entered here.
            </p>
          </>
        )}
        {kind !== "demo" && kind !== "holopod" && (
          <label className="field">
            {kind === "file" ? "File path on the worker" : "Stream URL"}
            <input type="text" value={url}
                   placeholder={kind === "file" ? "/data/clips/case-12.mp4" : "rtsp://10.0.4.7:554/stream1"}
                   onChange={(event) => setUrl(event.target.value)} />
          </label>
        )}
        {kind === "file" && (
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
            <span style={{ fontSize: 13 }}>Loop the file continuously</span>
          </label>
        )}
        {["rtsp", "srt"].includes(kind) && (
          <div className="row" style={{ gap: 12 }}>
            <label className="field" style={{ flex: 1 }}>
              Username
              <input type="text" value={username} onChange={(event) => setUsername(event.target.value)} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              Password
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </label>
          </div>
        )}
        <label className="field">
          Location
          <input type="text" value={location} placeholder="Operating room 2"
                 onChange={(event) => setLocation(event.target.value)} />
        </label>
        {["rtsp", "srt"].includes(kind) && (
          <p className="hint">
            Credentials are encrypted before they are stored and are only ever decrypted inside a worker.
            They are never sent back to a browser.
          </p>
        )}
        <div className="row">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !canSubmit} onClick={submit}>Add source</button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------- app catalog

export function Apps() {
  const { data, loading } = useAsync<AppManifest[]>(() => api.apps(), []);
  const [category, setCategory] = useState("");

  const categories = [...new Set((data ?? []).map((app) => app.category))];
  const apps = (data ?? []).filter((app) => !category || app.category === category);

  return (
    <div className="page stack">
      <PageHeader
        title="AI Apps"
        description="What HoloXI can do to a video stream. Availability reflects what the connected workers can actually run."
      />

      <div className="toolbar">
        <button className={`btn sm${category === "" ? " primary" : ""}`} onClick={() => setCategory("")}>All</button>
        {categories.map((option) => (
          <button key={option} className={`btn sm${category === option ? " primary" : ""}`}
                  onClick={() => setCategory(option)}>
            {option}
          </button>
        ))}
      </div>

      {loading && <Loading rows={4} />}

      {!loading && apps.length === 0 && (
        <Empty icon="apps" title="No apps in this category" body="Try another filter, or check that workers have published their catalog." />
      )}

      <div className="grid cols-3">
        {apps.map((app) => (
          <div className="card stack" key={app.app_id}>
            <div className="row">
              <span className={`led ${app.available ? "online" : "failed"}`} />
              <div className="card-title">{app.name}</div>
              <div className="spacer" />
              <span className="id">v{app.version}</span>
            </div>
            <p className="hint" style={{ margin: 0 }}>{app.description}</p>
            <div className="row wrap" style={{ gap: 5 }}>
              <span className="tag">{app.category}</span>
              <span className="tag">{app.compute.replace("_", " ")}</span>
              {app.requires_model && <span className="tag">needs a model</span>}
              {app.consumes.map((channel) => <span className="tag" key={channel}>reads {channel}</span>)}
            </div>
            {!app.available && (
              <p className="hint" style={{ color: "var(--coral)", margin: 0 }}>
                Missing on workers: {app.missing_requirements.join(", ")}
              </p>
            )}
            <details>
              <summary className="hint" style={{ cursor: "pointer" }}>
                Records {app.output_schema.length} field(s)
              </summary>
              <div className="log" style={{ marginTop: 8 }}>
                {app.output_schema.map((field) => (
                  <div className="log-line" key={field.name}>
                    <span style={{ color: "var(--signal)" }}>{field.name}</span>
                    <span className="log-time">{field.type}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ data

export function Data() {
  const [kind, setKind] = useState("");
  const [processId, setProcessId] = useState("");
  const processes = useAsync<ProcessSummary[]>(() => api.processes(), []);
  const query = [
    kind && `kind=${kind}`,
    processId && `process_id=${processId}`,
    "limit=500",
  ].filter(Boolean).join("&");
  const { data, error, loading, reload } = useAsync<Artifact[]>(
    () => api.browseArtifacts(query ? `?${query}` : ""), [kind, processId]);

  const artifacts = data ?? [];
  const images = artifacts.filter((artifact) => artifact.kind === "image");
  const rest = artifacts.filter((artifact) => artifact.kind !== "image");
  const imageGroups = groupImageArtifacts(images);
  const total = artifacts.reduce((sum, artifact) => sum + artifact.size_bytes, 0);

  return (
    <div className="page stack">
      <PageHeader
        title="Data"
        description="Everything HoloXI has produced. Files are organised by workspace, process, run and date."
      />

      <div className="toolbar">
        <select value={kind} onChange={(event) => setKind(event.target.value)} style={{ maxWidth: 180 }}>
          <option value="">All types</option>
          {["csv", "jsonl", "video", "image", "mask", "manifest"].map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <select value={processId} onChange={(event) => setProcessId(event.target.value)} style={{ maxWidth: 240 }}>
          <option value="">All processes</option>
          {(processes.data ?? []).map((process) => (
            <option key={process.id} value={process.id}>{process.name}</option>
          ))}
        </select>
        <div className="spacer" />
        <span className="hint">
          {rest.length} file{rest.length === 1 ? "" : "s"}
          {imageGroups.length > 0 && ` · ${images.length} image${images.length === 1 ? "" : "s"} in ${imageGroups.length} set${imageGroups.length === 1 ? "" : "s"}`}
          {" · "}{bytes(total)}
        </span>
      </div>

      {error && <ErrorBanner error={error} onRetry={reload} />}
      {loading && <Loading rows={5} />}

      {!loading && artifacts.length === 0 && (
        <Empty icon="data" title="No data yet" body="Run a process and its outputs will collect here." />
      )}

      {imageGroups.length > 0 && (kind === "" || kind === "image") && (
        <div className="stack" style={{ gap: 8 }}>
          <h2 style={{ margin: 0 }}>Image sets</h2>
          {imageGroups.map((group) => (
            <ImageArtifactSet key={group.key} group={group} />
          ))}
        </div>
      )}

      {rest.length > 0 && kind !== "image" && (
        <div className="card flush">
          <table>
            <thead>
              <tr><th>Kind</th><th>File</th><th>Size</th><th>Rows</th><th>Created</th><th /></tr>
            </thead>
            <tbody>
              {rest.map((artifact) => <ArtifactRow key={artifact.id} artifact={artifact} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type ImageGroup = {
  key: string;
  runId: string;
  appId: string | null;
  label: string;
  artifacts: Artifact[];
  bytes: number;
};

function groupImageArtifacts(images: Artifact[]): ImageGroup[] {
  const buckets = new Map<string, Artifact[]>();
  for (const artifact of images) {
    const key = `${artifact.run_id}::${artifact.app_id ?? "image"}`;
    const list = buckets.get(key) ?? [];
    list.push(artifact);
    buckets.set(key, list);
  }
  return [...buckets.entries()].map(([key, artifacts]) => {
    const appId = artifacts[0]?.app_id ?? null;
    const runId = artifacts[0]!.run_id;
    const label = appId === "dataset_collector"
      ? "Dataset collector"
      : appId === "snapshot"
        ? "Snapshots"
        : appId
          ? appId.replace(/_/g, " ")
          : "Images";
    return {
      key,
      runId,
      appId,
      label,
      artifacts,
      bytes: artifacts.reduce((sum, artifact) => sum + artifact.size_bytes, 0),
    };
  }).sort((a, b) => (b.artifacts[0]?.created_at ?? "").localeCompare(a.artifacts[0]?.created_at ?? ""));
}

function ImageArtifactSet({ group }: { group: ImageGroup }) {
  const zipUrl = api.zipArtifacts(group.runId, {
    kind: "image",
    appId: group.appId ?? undefined,
  });

  return (
    <details className="card">
      <summary className="card-title" style={{ cursor: "pointer" }}>
        <span style={{ flex: 1 }}>
          {group.label}
          <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>
            {group.artifacts.length} image{group.artifacts.length === 1 ? "" : "s"} · {bytes(group.bytes)} ·{" "}
            <span className="mono">{group.runId.replace("run_", "")}</span>
          </span>
        </span>
      </summary>
      <div className="stack" style={{ marginTop: 12, gap: 10 }}>
        <div className="row">
          <div className="spacer" />
          <a className="btn sm primary" href={zipUrl} download>
            Download zip
          </a>
        </div>
        <div className="card flush" style={{ border: "1px solid var(--line)" }}>
          <table>
            <thead>
              <tr><th>File</th><th>Size</th><th>Created</th><th /></tr>
            </thead>
            <tbody>
              {group.artifacts.map((artifact) => (
                <tr key={artifact.id}>
                  <td className="mono">{artifact.filename}</td>
                  <td className="mono">{bytes(artifact.size_bytes)}</td>
                  <td style={{ color: "var(--text-muted)" }}>{relative(artifact.created_at)}</td>
                  <td style={{ textAlign: "right" }}>
                    <a className="btn sm" href={`${api.base}${artifact.download_url}`} download>Download</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

// ------------------------------------------------------------------ sync

export function Sync() {
  const groups = useAsync<SyncGroup[]>(() => api.syncGroups(), []);
  const processes = useAsync<ProcessSummary[]>(() => api.processes(), []);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [removingGroup, setRemovingGroup] = useState<SyncGroup | null>(null);
  const [removingSession, setRemovingSession] = useState<{ id: string; label: string } | null>(null);

  return (
    <div className="page stack">
      <PageHeader
        title="Sync Sessions"
        description="Start several processes together and keep their results on one timeline. HoloXI records the real offset between them rather than assuming they started at the same instant."
        actions={<button className="btn primary" onClick={() => setCreating(true)}><Icon name="plus" size={14} />New sync group</button>}
      />

      {groups.loading && <Loading rows={3} />}
      {!groups.loading && (groups.data?.length ?? 0) === 0 && (
        <Empty
          icon="sync"
          title="No sync groups"
          body="Group processes that need to run together — for example a room camera and an instrument feed."
          action={<button className="btn primary" onClick={() => setCreating(true)}><Icon name="plus" size={14} />New sync group</button>}
        />
      )}

      <div className="stack">
        {(groups.data ?? []).map((group) => {
          const live = group.last_session
            && ["PENDING", "STARTING", "RUNNING", "STOPPING"].includes(group.last_session.state);
          return (
            <div className="card stack" key={group.id}>
              <div className="row">
                <div>
                  <div className="card-title">{group.name}</div>
                  <div className="hint">{group.process_ids.length} process(es)</div>
                </div>
                <div className="spacer" />
                {live ? (
                  <button className="btn danger" onClick={async () => {
                    await api.stopSyncSession(group.last_session!.id);
                    groups.reload();
                  }}>
                    Stop session
                  </button>
                ) : (
                  <button className="btn primary" onClick={async () => {
                    await api.startSyncGroup(group.id);
                    groups.reload();
                  }}>
                    Start together
                  </button>
                )}
                <button
                  className="btn ghost"
                  style={{ color: "var(--coral)" }}
                  disabled={Boolean(live)}
                  title={live ? "Stop the session before deleting this group" : undefined}
                  onClick={() => setRemovingGroup(group)}
                >
                  Delete group
                </button>
              </div>

              {group.last_session && (
                <SessionDetail
                  sessionId={group.last_session.id}
                  onDelete={() => setRemovingSession({
                    id: group.last_session!.id,
                    label: `${group.name} · ${group.last_session!.id}`,
                  })}
                />
              )}
            </div>
          );
        })}
      </div>

      {creating && (
        <Modal title="New sync group" onClose={() => setCreating(false)}>
          <div className="stack">
            <label className="field">
              Name
              <input type="text" value={name} placeholder="Case 42 — room + scope"
                     onChange={(event) => setName(event.target.value)} />
            </label>
            <div className="stat-label">Processes to start together</div>
            <div className="stack" style={{ gap: 6 }}>
              {(processes.data ?? []).map((process) => (
                <label className="row" key={process.id} style={{ gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(process.id)}
                    onChange={(event) =>
                      setSelected(event.target.checked
                        ? [...selected, process.id]
                        : selected.filter((id) => id !== process.id))
                    }
                  />
                  <span>{process.name}</span>
                  <span className="hint">{process.source_name}</span>
                </label>
              ))}
            </div>
            <div className="row">
              <div className="spacer" />
              <button className="btn ghost" onClick={() => setCreating(false)}>Cancel</button>
              <button
                className="btn primary"
                disabled={!name || selected.length < 2}
                onClick={async () => {
                  await api.createSyncGroup({ name, process_ids: selected });
                  setCreating(false);
                  setName("");
                  setSelected([]);
                  groups.reload();
                }}
              >
                Create group
              </button>
            </div>
          </div>
        </Modal>
      )}

      {removingGroup && (
        <ConfirmDelete
          title="Delete this sync group?"
          name={removingGroup.name}
          confirmLabel="Delete group"
          onClose={() => setRemovingGroup(null)}
          onConfirm={async () => {
            await api.deleteSyncGroup(removingGroup.id);
            groups.reload();
          }}
          body={
            <p style={{ marginTop: 0 }}>
              The group and its session history are removed. The individual process runs they
              started stay in run history.
            </p>
          }
        />
      )}

      {removingSession && (
        <ConfirmDelete
          title="Delete this sync session?"
          name={removingSession.id}
          confirmLabel="Delete session"
          onClose={() => setRemovingSession(null)}
          onConfirm={async () => {
            await api.deleteSyncSession(removingSession.id);
            groups.reload();
          }}
          body={
            <p style={{ marginTop: 0 }}>
              Remove <span className="mono">{removingSession.label}</span>. Process runs started by
              this session stay in run history; only the session grouping is deleted.
            </p>
          }
        />
      )}
    </div>
  );
}

function SessionDetail({ sessionId, onDelete }: { sessionId: string; onDelete: () => void }) {
  const { data } = usePolling(() => api.syncSession(sessionId), 5000);
  if (!data) return null;
  const runs = Object.entries(data.alignment?.runs ?? {});
  const live = ["PENDING", "STARTING", "RUNNING", "STOPPING"].includes(data.state);

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row">
        <StateBadge state={data.state} />
        <span className="hint">
          {data.run_ids.length} run(s) · measured spread {number(data.alignment?.max_skew_s, 3)}s
        </span>
        <div className="spacer" />
        <a className="btn sm" href={api.exportSession(sessionId)}>Export combined CSV</a>
        <button
          className="btn sm ghost"
          style={{ color: "var(--coral)" }}
          disabled={live}
          title={live ? "Stop the session before deleting it" : undefined}
          onClick={onDelete}
        >
          Delete session
        </button>
      </div>
      {runs.length > 0 && (
        <div className="card flush">
          <table>
            <thead>
              <tr><th>Run</th><th>State</th><th>First frame</th><th>Offset from T0</th></tr>
            </thead>
            <tbody>
              {runs.map(([runId, detail]: [string, any]) => (
                <tr key={runId}>
                  <td className="mono">{runId.replace("run_", "")}</td>
                  <td><StateBadge state={detail.state} /></td>
                  <td className="mono">{clockTime(detail.first_frame_at)}</td>
                  <td className="mono" style={{ color: "var(--signal)" }}>
                    {detail.offset_from_requested_s !== null
                      ? `${detail.offset_from_requested_s > 0 ? "+" : ""}${detail.offset_from_requested_s}s`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- models

export function Models() {
  const { data, loading, reload } = useAsync<ModelRow[]>(() => api.models(), []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", task: "detection", version: "1.0.0", local_path: "", labels: "" });
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="page stack">
      <PageHeader
        title="Models"
        description="Model files available to the apps that need one. Every result records the model version that produced it."
        actions={<button className="btn primary" onClick={() => setAdding(true)}><Icon name="plus" size={14} />Register model</button>}
      />

      {loading && <Loading rows={3} />}
      {!loading && (data?.length ?? 0) === 0 && (
        <Empty
          icon="models"
          title="No models registered"
          body="Classification, segmentation, pose and custom-model apps need an ONNX file before they can run."
          action={<button className="btn primary" onClick={() => setAdding(true)}><Icon name="plus" size={14} />Register model</button>}
        />
      )}

      {(data?.length ?? 0) > 0 && (
        <div className="card flush">
          <table>
            <thead>
              <tr><th>Model</th><th>Task</th><th>Framework</th><th>Version</th><th>Available</th></tr>
            </thead>
            <tbody>
              {data!.map((model) => (
                <tr key={model.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{model.name}</div>
                    <div className="hint">{model.description}</div>
                  </td>
                  <td><span className="tag">{model.task}</span></td>
                  <td className="mono">{model.framework}</td>
                  <td className="mono">v{model.latest_version}</td>
                  <td>
                    {model.versions[0]?.available
                      ? <span className="row" style={{ gap: 6 }}><span className="led online" />ready</span>
                      : <span className="row" style={{ gap: 6 }}><span className="led failed" />file missing</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <Modal title="Register a model" onClose={() => setAdding(false)}>
          <div className="stack">
            {error && <ErrorBanner error={error} />}
            <label className="field">
              Name
              <input type="text" value={form.name} placeholder="Instrument detector"
                     onChange={(event) => setForm({ ...form, name: event.target.value })} />
            </label>
            <div className="row" style={{ gap: 12 }}>
              <label className="field" style={{ flex: 1 }}>
                Task
                <select value={form.task} onChange={(event) => setForm({ ...form, task: event.target.value })}>
                  {["detection", "classification", "segmentation", "pose", "custom"].map((task) => (
                    <option key={task} value={task}>{task}</option>
                  ))}
                </select>
              </label>
              <label className="field" style={{ flex: 1 }}>
                Version
                <input type="text" value={form.version}
                       onChange={(event) => setForm({ ...form, version: event.target.value })} />
              </label>
            </div>
            <label className="field">
              ONNX file path on the workers
              <input type="text" value={form.local_path} placeholder="/models/instruments-v1.onnx"
                     onChange={(event) => setForm({ ...form, local_path: event.target.value })} />
              <span className="hint">
                Mount the same path on every worker, or upload to storage and use the storage key.
              </span>
            </label>
            <label className="field">
              Labels
              <input type="text" value={form.labels} placeholder="scalpel, forceps, gauze"
                     onChange={(event) => setForm({ ...form, labels: event.target.value })} />
            </label>
            <div className="row">
              <div className="spacer" />
              <button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn primary" disabled={!form.name} onClick={async () => {
                try {
                  await api.createModel({
                    ...form,
                    framework: "onnx",
                    labels: form.labels.split(",").map((label) => label.trim()).filter(Boolean),
                  });
                  setAdding(false);
                  reload();
                } catch (err) { setError((err as Error).message); }
              }}>
                Register
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --------------------------------------------------------------- compute

export function Compute() {
  const [refresh, setRefresh] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const { data } = usePolling<WorkerRow[]>(() => api.workers(), 5000, true, [refresh]);
  const storage = useAsync<StorageDestination[]>(() => api.storage(), []);

  const activeWorkers = (data ?? []).filter((worker) => worker.status !== "OFFLINE");
  const offlineWorkers = (data ?? []).filter((worker) => worker.status === "OFFLINE");

  const refreshNow = async () => {
    setRefreshing(true);
    setRefresh((value) => value + 1);
    // Brief busy state so the click feels acknowledged; polling restarts immediately.
    window.setTimeout(() => setRefreshing(false), 400);
  };

  return (
    <div className="page stack">
      <PageHeader
        title="Compute"
        description="The machines that run your processes. You do not normally need this page — HoloXI places work automatically — but it is here when you want to know where something ran."
        actions={
          <button className="btn" disabled={refreshing} onClick={refreshNow}>
            <Icon name="refresh" size={14} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        }
      />

      <div className="toolbar" style={{ alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Active workers</h2>
        <div className="spacer" />
        <span className="hint">{activeWorkers.length} connected</span>
      </div>

      <div className="card flush">
        {activeWorkers.length === 0 ? (
          <div style={{ padding: 18 }}>
            <p className="hint" style={{ margin: 0 }}>
              {(data?.length ?? 0) > 0
                ? "No workers are online right now."
                : "No workers have checked in yet."}
            </p>
          </div>
        ) : (
          <WorkerTable workers={activeWorkers} />
        )}
      </div>

      {offlineWorkers.length > 0 && (
        <details className="card worker-history">
          <summary>
            <div>
              <div className="card-title">Worker history</div>
              <div className="hint">Previously connected workers and their last heartbeat.</div>
            </div>
            <span className="tag">{offlineWorkers.length} offline</span>
          </summary>
          <div className="worker-history-table">
            <WorkerTable workers={offlineWorkers} historical />
          </div>
        </details>
      )}

      <h2>Storage destinations</h2>
      <div className="card flush">
        <table>
          <thead>
            <tr><th>Name</th><th>Provider</th><th>Default</th><th>Used</th></tr>
          </thead>
          <tbody>
            {(storage.data ?? []).map((destination) => (
              <tr key={destination.id}>
                <td>{destination.name}</td>
                <td className="mono">{destination.provider}</td>
                <td>{destination.is_default ? <span className="tag">default</span> : ""}</td>
                <td className="mono">{bytes(destination.bytes_used)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WorkerTable({ workers, historical = false }: {
  workers: WorkerRow[];
  historical?: boolean;
}) {
  return (
    <table>
      <thead>
        <tr>
          <th>Worker</th>
          {!historical && <th>Status</th>}
          {!historical && <th>Capacity</th>}
          <th>Hardware</th>
          <th>Runtimes</th>
          <th>{historical ? "Last seen" : "Heartbeat"}</th>
        </tr>
      </thead>
      <tbody>
        {workers.map((worker) => (
          <tr key={worker.id}>
            <td>
              <div className="mono">{worker.id}</div>
              <div className="hint">{worker.hostname}</div>
            </td>
            {!historical && (
              <td>
                <span className="row" style={{ gap: 6 }}>
                  <span className={`led ${worker.status === "BUSY" ? "running" : "online"}`} />
                  {worker.status.toLowerCase()}
                </span>
              </td>
            )}
            {!historical && <td className="mono">{worker.active_runs}/{worker.max_runs}</td>}
            <td className="hint">
              {worker.capabilities.cpu_cores ?? "?"} cores · {number(worker.capabilities.ram_mb)} MB
              {worker.capabilities.gpu ? ` · GPU (${worker.capabilities.gpu_name ?? "available"})` : " · CPU only"}
            </td>
            <td>
              <div className="row wrap" style={{ gap: 4 }}>
                {(worker.capabilities.runtimes ?? []).map((runtime) => (
                  <span className="tag" key={runtime}>{runtime}</span>
                ))}
              </div>
            </td>
            <td className="hint">{relative(worker.last_heartbeat_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------- events

export function Events() {
  const [severity, setSeverity] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [clearing, setClearing] = useState(false);
  const { data } = usePolling<EventRow[]>(
    () => api.events(severity ? `?severity=${severity}` : ""),
    6000,
    true,
    [severity, refresh],
  );
  const count = data?.length ?? 0;

  return (
    <div className="page stack">
      <PageHeader
        title="Events"
        description="Everything the platform noticed: starts, stops, disconnections, recoveries and failures."
        actions={
          <button
            className="btn ghost"
            style={{ color: "var(--coral)" }}
            disabled={count === 0}
            onClick={() => setClearing(true)}
          >
            <Icon name="trash" size={13} />
            Clear{severity ? ` ${severity}` : ""} events
          </button>
        }
      />

      <div className="toolbar">
        <Segmented
          value={severity}
          onChange={setSeverity}
          options={[
            { value: "", label: "All" },
            { value: "info", label: "Info" },
            { value: "warning", label: "Warning" },
            { value: "error", label: "Error" },
            { value: "critical", label: "Critical" },
          ]}
        />
      </div>

      {count === 0 ? (
        <Empty icon="events" title="No events" body="Starts, stops and recoveries will appear here as the platform notices them." />
      ) : (
        <div className="card flush">
          <table>
            <thead>
              <tr><th>Time</th><th>Type</th><th>Message</th><th>Run</th></tr>
            </thead>
            <tbody>
              {data!.map((event) => (
                <tr key={event.id}>
                  <td className="mono">{clockTime(event.timestamp)}</td>
                  <td>
                    <span className={`log-type ${event.severity}`} style={{ fontFamily: "var(--mono)", fontSize: 11 }}>
                      {event.event_type}
                    </span>
                  </td>
                  <td style={{ color: "var(--text-muted)" }}>{event.message}</td>
                  <td>
                    {event.run_id
                      ? <Link className="id" to={`/runs/${event.run_id}`}>{event.run_id.replace("run_", "")}</Link>
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {clearing && (
        <ConfirmDelete
          title={severity ? `Clear ${severity} events?` : "Clear all events?"}
          name="clear"
          confirmLabel={severity ? `Clear ${severity} events` : "Clear all events"}
          onClose={() => setClearing(false)}
          onConfirm={async () => {
            await api.clearEvents(severity);
            setRefresh((value) => value + 1);
          }}
          body={
            <p style={{ marginTop: 0 }}>
              {severity
                ? <>Every <span className="mono">{severity}</span> event in this workspace will be removed.</>
                : "Every event in this workspace will be removed."}
              {" "}Run history, artifacts and the audit log are not affected.
            </p>
          }
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------- settings

export function Settings({ workspaces, activeWorkspace }: {
  workspaces: { id: string; name: string; role?: string }[];
  activeWorkspace: string;
}) {
  const audit = useAsync(() => api.audit().catch(() => []), []);
  const [clearing, setClearing] = useState(false);
  const current = workspaces.find((workspace) => workspace.id === activeWorkspace);
  const canClear = current?.role === "admin";
  const count = audit.data?.length ?? 0;

  return (
    <div className="page stack">
      <PageHeader
        title="Settings"
        description="Workspace, access and the record of who changed what."
      />

      <div className="grid cols-2">
        <div className="card stack">
          <div className="card-title">Workspace</div>
          <table>
            <tbody>
              <tr><td className="hint">Name</td><td style={{ textAlign: "right" }}>{current?.name}</td></tr>
              <tr><td className="hint">Your role</td><td style={{ textAlign: "right" }}><span className="tag">{current?.role}</span></td></tr>
              <tr><td className="hint">Workspace ID</td><td className="mono" style={{ textAlign: "right" }}>{activeWorkspace}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="card stack">
          <div className="card-title">Roles</div>
          <table>
            <tbody>
              <tr><td className="mono">admin</td><td className="hint">Everything, including members and storage</td></tr>
              <tr><td className="mono">operator</td><td className="hint">Build, start and stop processes</td></tr>
              <tr><td className="mono">viewer</td><td className="hint">Watch and download; cannot change anything</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="row wrap" style={{ alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Activity log</h2>
        <div className="spacer" />
        {canClear && (
          <button
            className="btn ghost sm"
            style={{ color: "var(--coral)" }}
            disabled={count === 0}
            onClick={() => setClearing(true)}
          >
            Clear activity log
          </button>
        )}
      </div>
      <div className="card flush">
        {count === 0 ? (
          <div style={{ padding: 18 }}>
            <p className="hint" style={{ margin: 0 }}>
              No entries, or your role cannot read the activity log.
            </p>
          </div>
        ) : (
          <table>
            <thead>
              <tr><th>Time</th><th>Action</th><th>Target</th></tr>
            </thead>
            <tbody>
              {audit.data!.slice(0, 50).map((entry: any) => (
                <tr key={entry.id}>
                  <td className="mono">{clockTime(entry.timestamp)}</td>
                  <td className="mono">{entry.action}</td>
                  <td className="id">{entry.target_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {clearing && (
        <ConfirmDelete
          title="Clear the activity log?"
          name="clear"
          confirmLabel="Clear activity log"
          onClose={() => setClearing(false)}
          onConfirm={async () => {
            await api.clearAudit();
            audit.reload();
          }}
          body={
            <p style={{ marginTop: 0 }}>
              Every activity-log entry in this workspace will be removed. A single record of this
              clear is kept. Events, runs and stored data are not affected.
            </p>
          }
        />
      )}
    </div>
  );
}
